import {confirmDiscardCommentDraft} from 'helpers/commentDraft';
import {atom} from 'jotai';

export interface OptimisticComment {
  text: string;
  authorId: string;
  authorName?: string;
  createdAt: string;
}

export interface CommentState {
  threadId: string;
  activationKey?: string;
  editorKey?: string;
  planningCommentOriginId?: string | null;
  optimisticComment?: OptimisticComment;
  activationId?: number;
  source?: CommentOpenSource;
  scrollIntoView?: boolean;
}

export type CommentOpenSource =
  | 'url'
  | 'notification'
  | 'preview'
  | 'highlight'
  | 'drawer'
  | 'create'
  | 'programmatic';

export type OpenCommentState = Omit<CommentState, 'activationId'>;

type ActiveCommentUpdate =
  | CommentState
  | null
  | ((current: CommentState | null) => CommentState | null);

const activeCommentStateAtom = atom<CommentState | null>(null);
const commentThreadEditorKeyAtom = atom<Record<string, string>>({});

export const commentDraftDirtyAtom = atom(false);
export const pendingDeleteCommentIdsAtom = atom<Record<string, true>>({});
export const activeCommentAtom = atom<CommentState | null, [ActiveCommentUpdate], boolean>(
  (get) => get(activeCommentStateAtom),
  (get, set, update) => {
    const currentComment = get(activeCommentStateAtom);
    const nextComment = typeof update === 'function' ? update(currentComment) : update;
    const isSameCommentThread = currentComment?.threadId === nextComment?.threadId;

    if (get(commentDraftDirtyAtom) && !isSameCommentThread && !confirmDiscardCommentDraft()) {
      return false;
    }

    if (!isSameCommentThread) {
      set(commentDraftDirtyAtom, false);
    }

    set(activeCommentStateAtom, nextComment);
    return true;
  }
);
export const focusedThreadAtom = atom<string | undefined>(undefined);

export const markCommentPendingDeleteAtom = atom(null, (_get, set, commentId: string) => {
  set(pendingDeleteCommentIdsAtom, (current) => ({
    ...current,
    [commentId]: true,
  }));
});

export const clearCommentPendingDeleteAtom = atom(null, (_get, set, commentId: string) => {
  set(pendingDeleteCommentIdsAtom, (current) => {
    if (!current[commentId]) {
      return current;
    }

    const next = {...current};
    delete next[commentId];
    return next;
  });
});

let nextCommentActivationId = 0;

export const openCommentAtom = atom(null, (get, set, nextComment: OpenCommentState) => {
  nextCommentActivationId += 1;
  const editorKey = nextComment.editorKey ?? get(commentThreadEditorKeyAtom)[nextComment.threadId];
  const didOpen = set(activeCommentAtom, {
    ...nextComment,
    editorKey,
    activationId: nextCommentActivationId,
    scrollIntoView: nextComment.scrollIntoView ?? true,
  });

  if (didOpen && editorKey) {
    set(commentThreadEditorKeyAtom, (current) =>
      current[nextComment.threadId] === editorKey
        ? current
        : {...current, [nextComment.threadId]: editorKey}
    );
  }

  return didOpen;
});

export const closeCommentAtom = atom(null, (_get, set) => {
  return set(activeCommentAtom, null);
});
